import { useCallback, useEffect, useRef, useState } from 'react'

export const useScrollActivity = () => {
  const [isScrolledAllView, setIsScrolledAllView] = useState(false)
  const elRef = useRef<HTMLElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const mutationObserverRef = useRef<MutationObserver | null>(null)
  const scrollHandlerRef = useRef<((e: Event) => void) | null>(null)
  const rafRef = useRef<number>(0)

  const [hasScroll, setHasScroll] = useState(false)
  const [isScrollDown, setIsScrollDown] = useState(false)

  const setRef = useCallback((node: HTMLElement | null) => {
    if (elRef.current) {
      if (scrollHandlerRef.current) {
        elRef.current.removeEventListener('scroll', scrollHandlerRef.current)
      }
      resizeObserverRef.current && resizeObserverRef.current.disconnect()
      mutationObserverRef.current && mutationObserverRef.current.disconnect()
      rafRef.current && cancelAnimationFrame(rafRef.current)
      resizeObserverRef.current = null
      mutationObserverRef.current = null
      scrollHandlerRef.current = null
    }

    elRef.current = node

    if (!node) {
      setHasScroll(false)
      setIsScrollDown(false)
      return
    }

    const updateState = () => {
      const el = elRef.current
      if (!el) return
      const scrollable = el.scrollHeight > el.clientHeight + 1
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10
      setHasScroll(scrollable)
      setIsScrollDown(scrollable && atBottom)
    }

    const onScroll = () => {
      rafRef.current && cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(updateState)
    }

    scrollHandlerRef.current = onScroll
    node.addEventListener('scroll', onScroll, { passive: true })

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => updateState())
      resizeObserverRef.current = ro
      ro.observe(node)
    }

    if (typeof MutationObserver !== 'undefined') {
      const mo = new MutationObserver(() => updateState())
      mutationObserverRef.current = mo
      mo.observe(node, { childList: true, subtree: true, attributes: true })
    }

    updateState()
  }, [])

  useEffect(() => {
    return () => setRef(null)
  }, [setRef])

  useEffect(() => {
    if (isScrollDown && !isScrolledAllView) {
      setIsScrolledAllView(true)
    }
  }, [isScrollDown, setIsScrolledAllView, isScrolledAllView])

  useEffect(() => {
    setIsScrolledAllView(!hasScroll)
  }, [hasScroll, setIsScrolledAllView])

  return {
    ref: setRef,
    hasScroll,
    isScrollDown,
    isScrolledAllView,
  }
}
